import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/auth";

const ORG_TYPES = [
  { v: "nonprofit", l: "Nonprofit / Charity" },
  { v: "hospital", l: "Hospital / Cancer Centre" },
  { v: "cancer_organization", l: "Cancer Organisation" },
  { v: "business", l: "Business" },
  { v: "service_provider", l: "Service Provider" },
  { v: "community_organization", l: "Community Organisation" },
  { v: "vendor", l: "Vendor" },
  { v: "other", l: "Other" },
];

const DOMAINS = [
  { v: "legal", l: "Legal" },
  { v: "housing", l: "Housing" },
  { v: "immigration", l: "Immigration" },
  { v: "retail", l: "Retail" },
  { v: "nourishment", l: "Nourishment" },
  { v: "wellness", l: "Wellness & Wellbeing" },
  { v: "trainers", l: "Personal Trainers" },
  { v: "coaches", l: "Coaches" },
  { v: "therapists", l: "Therapists" },
  { v: "aesthetics", l: "Aesthetics" },
];

function toggleIn(list, v) {
  const s = new Set(list || []);
  s.has(v) ? s.delete(v) : s.add(v);
  return Array.from(s);
}

export default function OrganizationApply() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    organization_name: "", organization_type: "nonprofit",
    website: "", primary_contact_name: "", email: "", phone: "",
    location: "", description: "", proposed_domain_keys: [],
    agreement_accepted: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreement_accepted) return toast.error("Please accept the partnership terms.");
    setBusy(true);
    try {
      await api.post("/ops/public/organization-application", form);
      setDone(true);
      toast.success("Your organisation application is with BTC.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <main className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-center">
          <CheckCircle2 className="w-16 h-16 text-[#23803F] mx-auto mb-6" />
          <h1 className="text-4xl font-extrabold text-[#0B132B] mb-4">Thank you.</h1>
          <p className="text-lg text-[#4B5563] mb-8">
            Your organisation application is with the BTC team. We review every
            partnership carefully. Approval to partner with BTC is a separate
            step from having your services shown to patients — BTC governs
            patient-facing content in its own review process.
          </p>
          <Link to="/" className="btc-btn-primary" data-testid="organization-apply-home">
            Back to BTC <ArrowRight className="w-4 h-4" />
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="organization-apply-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#2D9B4E]/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-[#23803F]" strokeWidth={1.75} />
          </div>
          <p className="btc-eyebrow">PARTNER WITH BTC</p>
        </div>
        <h1 className="text-4xl font-extrabold text-[#0B132B] mb-3">Bring your organisation to BTC.</h1>
        <p className="text-[#4B5563] leading-relaxed mb-10">
          BTC partners with charities, hospitals, service providers and
          community organisations to expand the support available to patients.
          Every partnership is reviewed carefully. Approval to partner is a
          separate decision from making any specific service patient-visible.
        </p>
        <form onSubmit={submit} className="space-y-6" data-testid="organization-apply-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Organisation name" required>
              <input required value={form.organization_name} onChange={(e) => set("organization_name", e.target.value)} className="btc-input" data-testid="organization-apply-name" />
            </Field>
            <Field label="Organisation type" required>
              <select value={form.organization_type} onChange={(e) => set("organization_type", e.target.value)} className="btc-input" data-testid="organization-apply-type">
                {ORG_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Website">
            <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="btc-input" data-testid="organization-apply-website" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Primary contact name" required>
              <input required value={form.primary_contact_name} onChange={(e) => set("primary_contact_name", e.target.value)} className="btc-input" data-testid="organization-apply-contact" />
            </Field>
            <Field label="Contact email" required>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="btc-input" data-testid="organization-apply-email" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="btc-input" data-testid="organization-apply-phone" /></Field>
            <Field label="Location / service area"><input value={form.location} onChange={(e) => set("location", e.target.value)} className="btc-input" data-testid="organization-apply-location" /></Field>
          </div>
          <Field label="What does your organisation provide?" required>
            <textarea required minLength={10} rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="btc-input" data-testid="organization-apply-description" />
          </Field>
          <div>
            <label className="block text-sm font-semibold text-[#0B132B] mb-3">Which BTC support domains do you believe you support?</label>
            <div className="flex flex-wrap gap-2">
              {DOMAINS.map((d) => (
                <button type="button" key={d.v}
                  data-testid={`organization-domain-${d.v}`}
                  onClick={() => set("proposed_domain_keys", toggleIn(form.proposed_domain_keys, d.v))}
                  className={`px-4 py-2 rounded-full border text-sm font-semibold ${form.proposed_domain_keys.includes(d.v) ? "bg-[#2D9B4E] text-white border-[#2D9B4E]" : "border-gray-200 text-[#4B5563]"}`}
                >{d.l}</button>
              ))}
            </div>
            <p className="text-xs text-[#6B7280] mt-2">Approval to partner does not automatically make any of these areas patient-visible.</p>
          </div>
          <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl">
            <input type="checkbox" checked={form.agreement_accepted} onChange={(e) => set("agreement_accepted", e.target.checked)} data-testid="organization-apply-agreement" className="mt-1" />
            <span className="text-sm text-[#4B5563]">I confirm I have authority to represent this organisation and understand BTC governs its own patient-facing content and grounding decisions independently.</span>
          </label>
          <button type="submit" disabled={busy} className="btc-btn-primary w-full justify-center" data-testid="organization-apply-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit application <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[#0B132B] mb-2">
        {label}{required && <span className="text-[#B00020]"> *</span>}
      </span>
      {children}
    </label>
  );
}
